import { ReactNode } from 'react'

// ─────────────────────────────────────────────────────────────────────────────
// EmptyState — centered icon + title + description + optional action.
//
// Used wherever a surface has nothing to show: empty tables, empty lists, empty
// hero screens. Three visual modes:
//
//   • 'default' (idle empty): muted slate icon, muted text. Used by load tables,
//     thread lists, generic "nothing here yet" surfaces.
//   • 'loading': accent-colored icon + animated bouncing dots underneath. Used
//     while a pipeline is running. The icon is rendered in blue/violet to match
//     the scanning-state palette elsewhere in the v4 UI.
//   • 'hero' (centered page-level empty state): bigger icon block, larger
//     headline (text-3xl). Used by SearchAgentPanel / MainPanel's idle screens.
//
// Optional action slot accepts arbitrary children (typically a <Button>); rendered
// after the description with a top gap. When omitted, no action area renders.
//
// The component owns its own vertical centering — drop it inside a flex column
// and it expands to fill. Pass a `compact` prop to skip the height-expansion
// (useful inside narrow rails / popovers).
// ─────────────────────────────────────────────────────────────────────────────

export interface EmptyStateProps {
    /** Optional icon (typically a Gravity or MUI icon). Wrapped in a tinted square
     *  when variant='hero'; rendered raw in default/loading modes. */
    icon?: ReactNode
    /** Main heading. Plain text or rich nodes (e.g. an interpolated search query). */
    title: ReactNode
    /** Sub-line under the title. Optional. */
    description?: ReactNode
    /** Optional action — typically a <Button>. Rendered with mt-4. */
    action?: ReactNode
    /** Visual mode. See file header. */
    variant?: 'default' | 'loading' | 'hero'
    /** Skip the flex-1 height expansion — useful in narrow rails. */
    compact?: boolean
    className?: string
}

const variantSizes: Record<NonNullable<EmptyStateProps['variant']>, { padding: string; gap: string; icon: string; title: string; description: string }> = {
    default: {
        padding: 'p-8',
        gap: 'gap-2',
        icon: 'text-slate-400 dark:text-slate-500',
        title: 'text-xs text-slate-500 dark:text-slate-400 font-medium',
        description: 'text-[11px] text-slate-400 dark:text-slate-500 max-w-[220px]',
    },
    loading: {
        padding: 'p-6',
        gap: 'gap-3',
        icon: 'text-blue-500 dark:text-blue-400',
        title: 'text-base font-medium text-slate-900 dark:text-slate-100',
        description: 'text-xs text-slate-500 dark:text-slate-400 max-w-[220px] leading-relaxed',
    },
    hero: {
        padding: 'p-12',
        gap: 'gap-3',
        icon: '',
        title: 'text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100',
        description: 'text-sm text-slate-500 dark:text-slate-400 max-w-md',
    },
}

export const EmptyState = ({ icon, title, description, action, variant = 'default', compact = false, className = '' }: EmptyStateProps) => {
    const tokens = variantSizes[variant]
    return (
        <div
            className={`${compact ? '' : 'flex-1 min-h-0'} flex flex-col items-center justify-center text-center ${tokens.padding} ${tokens.gap} ${className}`}
        >
            {variant === 'hero' && icon && (
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-500 text-white">{icon}</span>
            )}
            {variant !== 'hero' && icon && <span className={`shrink-0 ${tokens.icon}`}>{icon}</span>}
            {title && <div className={tokens.title}>{title}</div>}
            {description && <div className={tokens.description}>{description}</div>}
            {variant === 'loading' && (
                <span className="flex items-center gap-1 mt-1" aria-hidden>
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-300 animate-bounce [animation-delay:0ms]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-300 animate-bounce [animation-delay:150ms]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-300 animate-bounce [animation-delay:300ms]" />
                </span>
            )}
            {action && <div className="mt-4">{action}</div>}
        </div>
    )
}
